/* Shop SSE connection for catalog updates (`/events/:salesPointId`).
 *
 * Mirrors kiosk transport behavior: consume kiosk-scoped SSE and forward each
 * message as `websocket-message` CustomEvent so downstream catalog handlers can
 * reuse the same realtime update contract.
 *
 * G3a: fail-closed when Assert denies — realtime_device_transport required;
 * surface_kiosk OR surface_customer (defense-in-depth). Payment-only seed may
 * enable both surfaces + realtime; client skip is for true unentitled/drift
 * tenants, not a product carve-out that requires surface_kiosk alone. Catalog
 * poll remains the refresh path when SSE stays disconnected.
 *
 * G8: kiosk-comparable connect timeout (never OPEN) + periodic readyState
 * health-check so silent stream death reconnects without waiting for onerror.
 *
 * G10: pause EventSource while HTTP_STORM_DETECTED latch is armed (close + no
 * reconnect stampede; resume after remaining TTL / latch clear). pause sets
 * isConnected false so catalog poll resumes. Connect-refetch catch-up is owned
 * by useShopCatalogRealtimeSync Contract B: storm latch arm → stormCatchUpPending
 * bypass (not cooldown zero on every disconnect).
 */

import { useEffect, useState } from 'react';
import { API_ENDPOINTS } from 'pi-kiosk-shared';
import { useRuntimeConfig } from '../../../shared/providers/RuntimeConfigProvider.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import { probeCustomerKioskSseAccess } from '../../../shared/realtime/probeCustomerKioskSseAccess.js';
import {
  getHttpStormRetryAfterMs,
  isHttpStormLatched,
  subscribeHttpStormLatch,
} from '../../../shared/http/httpStormDetectedLatch.js';
import { normalizeShopSseEventData } from './normalizeShopSseEventData.js';

const SSE_RECONNECT_DELAY_MS = 3_000;

/** G8 — match kiosk: abort if EventSource never reaches OPEN. */
export const SSE_CONNECT_TIMEOUT_MS = 10_000;

/**
 * G8 — periodic readyState probe while the hook believes the stream is live.
 * Catches silent death where some browsers leave EventSource without onerror.
 */
export const SSE_HEALTH_CHECK_INTERVAL_MS = 30_000;

/** True only for a positive finite sales-point id (rejects null/undefined/NaN). */
export function isUsableShopKioskId(kioskId: number | null | undefined): kioskId is number {
  return typeof kioskId === 'number' && Number.isFinite(kioskId) && kioskId > 0;
}

export function useShopKioskSSE(kioskId: number | null | undefined): boolean {
  const { apiBaseUrl } = useRuntimeConfig();
  const tenantCode = useTenantCode();
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    const setConnectedGuarded = (next: boolean): void => {
      setIsConnected((prev) => (prev === next ? prev : next));
    };

    // `undefined <= 0` is false in JS — without this, String(undefined) → `/events/undefined` (422).
    if (!isUsableShopKioskId(kioskId)) {
      return;
    }

    const eventsPath = API_ENDPOINTS.EVENTS.replace(':salesPointId', String(kioskId));
    const baseUrl = apiBaseUrl.replace(/\/+$/, '');
    const sseUrl = `${baseUrl}/api/${encodeURIComponent(tenantCode)}${eventsPath}`;

    let cancelled = false;
    let entitlementDenied = false;
    let eventSource: EventSource | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let connectTimeoutTimer: ReturnType<typeof setTimeout> | null = null;
    let healthCheckTimer: ReturnType<typeof setInterval> | null = null;

    const clearReconnectTimer = (): void => {
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
    };

    const clearConnectTimeout = (): void => {
      if (connectTimeoutTimer !== null) {
        clearTimeout(connectTimeoutTimer);
        connectTimeoutTimer = null;
      }
    };

    const clearHealthCheck = (): void => {
      if (healthCheckTimer !== null) {
        clearInterval(healthCheckTimer);
        healthCheckTimer = null;
      }
    };

    const closeEventSource = (): void => {
      clearConnectTimeout();
      clearHealthCheck();
      if (eventSource !== null) {
        eventSource.close();
        eventSource = null;
      }
      if (!cancelled) {
        setConnectedGuarded(false);
      }
    };

    /** G10 — close live stream + cancel reconnect while latched (no stampede). */
    const pauseForStorm = (): void => {
      clearReconnectTimer();
      closeEventSource();
    };

    const scheduleReconnectAfterFailure = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      clearReconnectTimer();
      if (isHttpStormLatched()) {
        scheduleConnectAfterStorm();
        return;
      }
      void probeCustomerKioskSseAccess(sseUrl).then((access) => {
        if (cancelled || entitlementDenied) {
          return;
        }
        if (access === 'denied') {
          entitlementDenied = true;
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        reconnectTimer = setTimeout(connect, SSE_RECONNECT_DELAY_MS);
      });
    };

    const startHealthCheck = (): void => {
      clearHealthCheck();
      healthCheckTimer = setInterval(() => {
        if (cancelled || entitlementDenied || eventSource === null) {
          return;
        }
        if (isHttpStormLatched()) {
          pauseForStorm();
          scheduleConnectAfterStorm();
          return;
        }
        // Silent stream death: readyState left non-OPEN without a reliable onerror.
        if (eventSource.readyState !== EventSource.OPEN) {
          clearHealthCheck();
          clearConnectTimeout();
          if (!cancelled) {
            setConnectedGuarded(false);
          }
          eventSource.close();
          eventSource = null;
          scheduleReconnectAfterFailure();
        }
      }, SSE_HEALTH_CHECK_INTERVAL_MS);
    };

    const connect = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      if (isHttpStormLatched()) {
        scheduleConnectAfterStorm();
        return;
      }
      clearReconnectTimer();
      closeEventSource();
      eventSource = new EventSource(sseUrl);

      const markConnected = (): void => {
        if (cancelled || isHttpStormLatched()) {
          return;
        }
        clearConnectTimeout();
        setConnectedGuarded(true);
        startHealthCheck();
      };

      // G8 — never-open timeout (kiosk parity: 10s).
      connectTimeoutTimer = setTimeout(() => {
        connectTimeoutTimer = null;
        if (cancelled || entitlementDenied || eventSource === null) {
          return;
        }
        if (eventSource.readyState === EventSource.OPEN) {
          return;
        }
        clearHealthCheck();
        if (!cancelled) {
          setConnectedGuarded(false);
        }
        eventSource.close();
        eventSource = null;
        scheduleReconnectAfterFailure();
      }, SSE_CONNECT_TIMEOUT_MS);

      eventSource.onopen = (): void => {
        markConnected();
      };
      eventSource.onmessage = (event): void => {
        // Match kiosk: some browsers skip onopen; first message proves the stream is live.
        markConnected();
        window.dispatchEvent(
          new CustomEvent('websocket-message', {
            detail: { data: normalizeShopSseEventData(event.data) },
          }),
        );
      };
      eventSource.onerror = (): void => {
        clearConnectTimeout();
        clearHealthCheck();
        if (!cancelled) {
          setConnectedGuarded(false);
        }
        eventSource?.close();
        eventSource = null;
        if (cancelled || entitlementDenied) {
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        scheduleReconnectAfterFailure();
      };
    };

    /**
     * Resume after latch TTL (fair-queue twin: poll live latch — no notify-on-expire).
     * Extends wait if latch was re-armed while sleeping.
     */
    const scheduleConnectAfterStorm = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      clearReconnectTimer();
      const waitMs = Math.max(getHttpStormRetryAfterMs(), 1);
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        if (cancelled || entitlementDenied) {
          return;
        }
        if (isHttpStormLatched()) {
          scheduleConnectAfterStorm();
          return;
        }
        connect();
      }, waitMs);
    };

    const tryStart = (): void => {
      if (cancelled || entitlementDenied) {
        return;
      }
      if (isHttpStormLatched()) {
        pauseForStorm();
        scheduleConnectAfterStorm();
        return;
      }
      connect();
    };

    // G10 — do not probe/fetch while latched (guard throw → 'unknown' → connect stampede).
    if (isHttpStormLatched()) {
      tryStart();
    } else {
      void probeCustomerKioskSseAccess(sseUrl).then((access) => {
        if (cancelled) {
          return;
        }
        if (access === 'denied') {
          entitlementDenied = true;
          return;
        }
        tryStart();
      });
    }

    const unsubscribeLatch = subscribeHttpStormLatch(() => {
      if (cancelled || entitlementDenied) {
        return;
      }
      if (isHttpStormLatched()) {
        pauseForStorm();
        scheduleConnectAfterStorm();
        return;
      }
      // Latch cleared (tests / explicit clear) — cancel TTL resume timer, one connect.
      if (eventSource === null) {
        clearReconnectTimer();
        connect();
      }
    });

    return (): void => {
      cancelled = true;
      unsubscribeLatch();
      setConnectedGuarded(false);
      clearReconnectTimer();
      clearConnectTimeout();
      clearHealthCheck();
      eventSource?.close();
      eventSource = null;
    };
  }, [apiBaseUrl, tenantCode, kioskId]);

  return isConnected;
}
